import React from "react";
import { Badge, Dropdown, message } from "antd";
import {
  BellFilled,
  DownOutlined,
  LogoutOutlined,
  MenuOutlined,
  MessageFilled,
  UserOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getDisplayName, getInitial, getRoleLabel } from "../../helper/userDisplay";
import { userData } from "../../redux/userDataSlice";
import { auth, db } from "../../firebase";
import { uploadFile } from "../../helper/firebaseUpload";
import { useUnreadCount } from "../../helper/notifications";
import EditRoleModal from "../../pages/promoterList/updateUser/EditRoleModal";
import "./header.css";

const Header2 = ({
  title = "Overview",
  onBell,
  onMessages,
  showMenu = false,
  onMenu,
  onLogout,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userDataSlice?.userData);
  const [imageBroken, setImageBroken] = React.useState(false);
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const uid = user?.uuid || user?._id || auth.currentUser?.uid || null;
  const unreadCount = useUnreadCount(uid);
  // From the single layout-level listener (redux), not a per-header subscription.
  const unreadMessages = useSelector(
    (state) => state.userDataSlice?.unreadThreadIds?.length || 0,
  );

  // The redux user is a login-time snapshot; the admin's photo/name may have
  // changed since (set from the Users table, or by another admin), which is why
  // the header avatar could look empty while the Users table showed the right
  // photo. Refresh from Firestore on mount so the avatar + Edit-profile modal
  // reflect the current doc.
  React.useEffect(() => {
    if (!uid) return;
    getDoc(doc(db, "users", uid))
      .then((snap) => {
        if (snap.exists()) dispatch(userData({ ...user, ...snap.data() }));
      })
      .catch((e) => console.warn("header: profile refresh failed", e?.message));
    // run once per uid; merging the fresh doc must not loop (uuid is stable)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  // Self-service profile update through the shared Edit User modal: write the
  // logged-in admin's own users/{uid} doc, then refresh redux so the header
  // updates immediately.
  const handleProfileSubmit = async (values, imageFile, imageUrl) => {
    if (!uid) {
      message.error("You're not signed in.");
      return;
    }
    setSaving(true);
    try {
      const updates = {
        name: (values.name || "").trim(),
        role: values.role === "admin" ? "admin" : "employee",
        updatedAt: new Date().toISOString(),
      };
      if (imageUrl) {
        // Already uploaded on pick.
        updates.image = imageUrl;
      } else if (imageFile) {
        // Fallback: on-pick upload didn't finish — upload now.
        const url = await uploadFile(imageFile, "users");
        if (!url) throw new Error("Photo upload failed. Please try again.");
        updates.image = url;
      }
      await updateDoc(doc(db, "users", uid), updates);
      dispatch(userData({ ...(user || {}), ...updates }));
      message.success("Profile updated");
      setProfileOpen(false);
    } catch (e) {
      message.error(e.message || "Couldn't update profile");
    } finally {
      setSaving(false);
    }
  };

  const userMenuItems = [
    {
      key: "edit-profile",
      icon: <UserOutlined />,
      label: "Edit profile",
      onClick: () => setProfileOpen(true),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Log out",
      danger: true,
      onClick: () => onLogout?.(),
    },
  ];
  // Reset the broken-image flag whenever the avatar URL changes, otherwise a
  // single 404 would suppress every subsequent (valid) avatar — the header
  // never unmounts, so the flag would stick across user switches.
  React.useEffect(() => setImageBroken(false), [user?.image]);
  const name = getDisplayName(user);
  const role = getRoleLabel(user);
  const initial = getInitial(user);
  const showImage = user?.image && !imageBroken;

  return (
    <header className="layout-header">
      <div className="layout-header-left">
        {showMenu && (
          <button
            type="button"
            className="layout-header-menu"
            onClick={onMenu}
            aria-label="Open navigation"
          >
            <MenuOutlined />
          </button>
        )}
        <div className="layout-header-title">
          <span className="layout-header-eyebrow">2nd Home</span>
          <h2 className="layout-header-section">{title}</h2>
        </div>
      </div>

      <div className="layout-header-actions">
        <button
          type="button"
          className="layout-header-bell"
          onClick={onMessages}
          aria-label="Messages"
        >
          <Badge
            count={unreadMessages}
            overflowCount={99}
            size="small"
            color="#c2603d"
            offset={[-2, 2]}
          >
            <MessageFilled />
          </Badge>
        </button>
        <button
          type="button"
          className="layout-header-bell"
          onClick={onBell}
          aria-label="Notifications"
        >
          <Badge
            count={unreadCount}
            overflowCount={99}
            size="small"
            color="#c2603d"
            offset={[-2, 2]}
          >
            <BellFilled />
          </Badge>
        </button>
        <Dropdown
          menu={{ items: userMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <div
            className="layout-header-user"
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            aria-label="Account menu"
          >
            {showImage ? (
              <img
                className="layout-header-avatar layout-header-avatar-img"
                src={user.image}
                alt={name}
                onError={() => setImageBroken(true)}
              />
            ) : (
              <span className="layout-header-avatar">{initial}</span>
            )}
            <span className="layout-header-user-meta">
              <span className="layout-header-user-name">{name}</span>
              <span className="layout-header-user-role">{role}</span>
            </span>
            <DownOutlined className="layout-header-user-caret" />
          </div>
        </Dropdown>
      </div>

      <EditRoleModal
        visible={profileOpen}
        isLoading={saving}
        user={user}
        onCancel={() => setProfileOpen(false)}
        onSubmit={handleProfileSubmit}
        lockRole
      />
    </header>
  );
};

export default Header2;
